import React from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'antd-mobile';
import { isIOS } from '../../utils/deviceUtils';
import {AppStoreIcon, GooglePlayIcon} from "../../assets/icons";

interface UpgradeGuideContentProps {
    platform: 'ios' | 'android';
}

const UpgradeGuideContent: React.FC<UpgradeGuideContentProps> = ({ platform }) => {
    const { t } = useTranslation();
    const isIOSPlatform = platform === 'ios';

    const storeName = isIOSPlatform
        ? t('appVersion.guide.appStore')
        : t('appVersion.guide.googlePlay');

    const StoreIcon = isIOSPlatform ? AppStoreIcon : GooglePlayIcon;

    return (
        <div style={{ background: '#F0F0FA', borderRadius: 12, padding: '24px 16px' }}>
            {/* Title */}
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}>
                    {t('appVersion.guide.title')}
                </div>
                <div style={{ fontSize: 14, color: '#6B6B9E', marginTop: 4 }}>
                    {t('appVersion.guide.subtitle')}
                </div>
            </div>

            {/* Icon Flow: Open → Search → Update */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                margin: '20px 0',
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        overflow: 'hidden',
                        margin: '0 auto 4px',
                    }}>
                        <StoreIcon width={48}  height={48} />
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{t('appVersion.guide.open')}</div>
                </div>

                <div style={{ color: '#999', fontSize: 18 }}>→</div>

                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 4px',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <circle cx="11" cy="11" r="7" stroke="#666" strokeWidth="2" />
                            <line x1="16" y1="16" x2="21" y2="21" stroke="#666" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{t('appVersion.guide.search')}</div>
                </div>

                <div style={{ color: '#999', fontSize: 18 }}>→</div>

                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: isIOSPlatform ? '#007AFF' : '#01875F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 4px',
                        color: '#fff',
                        fontSize: 12,
                        fontWeight: 600,
                    }}>
                        Update
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>{t('appVersion.guide.update')}</div>
                </div>
            </div>

            {/* Steps */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Step 1 */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: '#E8E8F4',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#5B5B8A',
                            flexShrink: 0,
                        }}>1</span>
                        <span style={{ fontSize: 15, fontWeight: 500 }}>
                            {t('appVersion.guide.step1', { storeName })}
                        </span>
                    </div>
                </div>

                {/* Step 2 */}
                <div
                    style={{ background: '#fff', borderRadius: 12, padding: '12px 16px', cursor: 'pointer' }}
                    onClick={() => {
                        const textArea = document.createElement('textarea');
                        textArea.value = 'SwiftX Driver';
                        textArea.style.position = 'fixed';
                        textArea.style.opacity = '0';
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        Toast.show({ content: t('appVersion.guide.copied') });
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: '#E8E8F4',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#5B5B8A',
                            flexShrink: 0,
                        }}>2</span>
                        <span style={{ fontSize: 15, fontWeight: 500, flex: 1 }}>
                            {t('appVersion.guide.step2')}
                        </span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                            <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="#999" strokeWidth="1.2" />
                            <path d="M10.5 5.5V3.5C10.5 2.67 9.83 2 9 2H3.5C2.67 2 2 2.67 2 3.5V9C2 9.83 2.67 10.5 3.5 10.5H5.5" stroke="#999" strokeWidth="1.2" />
                        </svg>
                    </div>
                </div>

                {/* Step 3 */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: '#E8E8F4',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#5B5B8A',
                            flexShrink: 0,
                        }}>3</span>
                        <span style={{ fontSize: 15, fontWeight: 500 }}>
                            {t('appVersion.guide.step3')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpgradeGuideContent;
